import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ArrowLeft, Calendar, Building2, Euro, Ship, Zap, Leaf, TrendingUp, Truck, Users } from 'lucide-react';
import { useLanguage, LanguageSwitcher } from '@/components/LanguageContext';

const Article2 = () => {
  const { t, language } = useLanguage();
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-white">
      <div className="container mx-auto px-6 py-8">
        {/* Header */}
        <div className="mb-8">
          <Link to="/">
            <Button variant="outline" className="mb-4">
              <ArrowLeft className="h-4 w-4 mr-2" />
              {t('common.backToHome')}
            </Button>
          </Link>
          
          <div className="flex items-center mb-4">
            <Badge className="bg-green-600 text-white mr-4">
              <Building2 className="h-3 w-3 mr-1" />
              Infrastructure
            </Badge>
            <span className="text-sm text-gray-600">Kuehne+Nagel News</span>
          </div>
          
          <div className="flex items-center text-sm text-muted-foreground mb-6">
            <Calendar className="h-4 w-4 mr-1" />
            February 2026
            <span className="mx-2">•</span>
            <span>6 min read</span>
          </div>
          
          <div className="flex justify-end mb-4">
            <LanguageSwitcher />
          </div>
        </div>

        {/* Article Content */}
        <article className="max-w-4xl mx-auto">
          <h1 className="text-4xl font-bold text-gray-900 mb-6">
            {t('article.2.title')}
          </h1>
          
          <img 
            src="https://dhl-freight-connections.com/wp-content/uploads/2026/02/LAY_DHL_Hylane_Hydrogen_01-1536x864.jpg" 
            alt="DHL Hydrogen Truck"
            className="w-full h-64 object-cover rounded-lg mb-8"
          />

          <div className="prose prose-lg max-w-none">
            <p className="text-xl text-gray-700 mb-6 font-medium">
              {t('article.2.intro')}
            </p>

            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">{t('article.2.section1.title')}</h2>
            
            <p className="mb-6">
              {t('article.2.p1')}
            </p>

            <p className="mb-6">
              {t('article.2.p2')}
            </p>

            <div className="bg-green-50 p-6 rounded-lg my-8 border border-green-100">
              <h3 className="font-bold text-green-900 mb-2 flex items-center">
                <Euro className="mr-2" />
                {t('article.2.highlight.title')}
              </h3>
              <p className="text-sm text-green-800">
                {t('article.2.highlight.content')}
              </p>
            </div>

            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">{t('article.2.section2.title')}</h2>
            
            <p className="mb-6">
              {t('article.2.p3')}
            </p>

            <p className="mb-6">
              {t('article.2.p4')}
            </p>

            <div className="bg-blue-50 p-6 rounded-lg my-8">
              <h3 className="font-bold text-blue-900 mb-4">{language === 'ko' ? '파트너십 협력 세부사항' : 'Partnership Collaboration Details'}</h3>
              <div className="grid md:grid-cols-2 gap-4 text-sm text-blue-800">
                <div>
                  <h4 className="font-semibold mb-2 flex items-center">
                    <Truck className="mr-1 h-4 w-4" />
                    {language === 'ko' ? 'hylane 서비스 모델' : 'hylane Service Model'}
                  </h4>
                  <ul className="space-y-1">
                    <li><strong>{language === 'ko' ? '서비스 모델:' : 'Service Model:'}</strong> {language === 'ko' ? 'Transport-as-a-Service' : 'Transport-as-a-Service'}</li>
                    <li><strong>{language === 'ko' ? '비용 구조:' : 'Cost Structure:'}</strong> {language === 'ko' ? '주행 킬로미터당 지불' : 'Pay per kilometer driven'}</li>
                    <li><strong>{language === 'ko' ? '유연성:' : 'Flexibility:'}</strong> {language === 'ko' ? '초기 투자 없이 평가 가능' : 'Evaluate without upfront investment'}</li>
                    <li><strong>{language === 'ko' ? '설립:' : 'Founded:'}</strong> {language === 'ko' ? '2021년 쿨른 설립' : 'Founded 2021 in Cologne'}</li>
                  </ul>
                </div>
                <div>
                  <h4 className="font-semibold mb-2 flex items-center">
                    <Users className="mr-1 h-4 w-4" />
                    {language === 'ko' ? '운영 파트너' : 'Operational Partners'}
                  </h4>
                  <ul className="space-y-1">
                    <li><strong>{language === 'ko' ? 'Schuurman Logistics:' : 'Schuurman Logistics:'}</strong> {language === 'ko' ? '운전자 및 일일 운영' : 'Driver and daily operations'}</li>
                    <li><strong>{language === 'ko' ? 'Teal Mobility:' : 'Teal Mobility:'}</strong> {language === 'ko' ? '수소 충전 인프라' : 'Hydrogen refueling infrastructure'}</li>
                    <li><strong>{language === 'ko' ? '합작 벤처:' : 'Joint Venture:'}</strong> {language === 'ko' ? 'Air Liquide & TotalEnergies' : 'Air Liquide & TotalEnergies'}</li>
                    <li><strong>{language === 'ko' ? '목표:' : 'Goal:'}</strong> {language === 'ko' ? '유럽 주요 도로 수소 스테이션 네트워크' : 'European road corridor H2 stations'}</li>
                  </ul>
                </div>
              </div>
            </div>

            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">{t('article.2.section3.title')}</h2>
            
            <p className="mb-6">
              {t('article.2.p5')}
            </p>

            <p className="mb-6">
              {t('article.2.p6')}
            </p>

            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">{t('article.2.section4.title')}</h2>
            
            <p className="mb-6">
              {t('article.2.p7')}
            </p>

            <p className="mb-6">
              {t('article.2.p8')}
            </p>

            <div className="bg-green-50 p-6 rounded-lg mt-8">
              <h3 className="font-bold text-green-900 mb-4">{language === 'ko' ? '수소 트럭 운영 세부사항' : 'Hydrogen Truck Operations'}</h3>
              <div className="space-y-4 text-sm text-green-800">
                <div>
                  <h4 className="font-semibold mb-2 flex items-center">
                    <Zap className="mr-1 h-4 w-4" />
                    {language === 'ko' ? '운영 사양' : 'Operational Specifications'}
                  </h4>
                  <p>{language === 'ko' ? 
                    '수소 동력 트럭은 DHL Freight의 아인트호벤 운영에 배치되어 네덜란드 여러 주에서 매일 약 300킬로미터를 운행하며, 중거리 화물 운송에서 수소 기술의 실행 가능성을 보여줍니다.' : 
                    `The hydrogen-powered truck is deployed in DHL Freight's Eindhoven operations, covering approximately 300 kilometers daily across Dutch provinces, demonstrating the viability of hydrogen technology in medium-distance freight transport.`
                  }</p>
                </div>
                <div>
                  <h4 className="font-semibold mb-2 flex items-center">
                    <Leaf className="mr-1 h-4 w-4" />
                    {language === 'ko' ? 'Hylane 파트너십 모델' : 'Hylane Partnership Model'}
                  </h4>
                  <p>{language === 'ko' ? 
                    'DHL Freight는 수소 모빌리티 솔루션의 선도적 제공업체인 Hylane과 파트너십을 맺어 킬로미터당 지불 서비스 모델을 제공하여 상당한 초기 자본 투자의 필요성을 제거합니다.' : 
                    `DHL Freight has partnered with Hylane, a leading provider of hydrogen mobility solutions, offering a pay-per-kilometer service model that eliminates the need for significant upfront capital investment.`
                  }</p>
                </div>
              </div>
            </div>
          </div>

          {/* Source Attribution */}
          <div className="mt-12 p-6 bg-gray-50 rounded-lg">
            <p className="text-sm text-gray-600 mb-2">
              {t('common.sourceAttribution')} DHL Freight Connections - First Hydrogen Truck Pilot in Netherlands.
            </p>
            <p className="text-sm text-gray-600 mb-4">
              {t('common.sourceDetails')}
            </p>
            <a 
              href="https://dhl-freight-connections.com/en/business/first-hydrogen-truck-pilot-in-netherlands/" 
              target="_blank" 
              rel="noopener noreferrer"
              className="inline-block"
            >
              <Button className="bg-green-600 hover:bg-green-700 text-white">
                {t('common.readOriginalArticle')}
              </Button>
            </a>
          </div>

          {/* Navigation */}
          <div className="mt-8 flex justify-between">
            <Link to="/">
              <Button variant="outline">
                <ArrowLeft className="h-4 w-4 mr-2" />
                {t('common.backToAllArticles')}
              </Button>
            </Link>
          </div>
        </article>
      </div>
    </div>
  );
};

export default Article2;